import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Building2, Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { tenantService, type TenantDto } from '@/services/tenants';
import { authService } from '@/services/auth';
import { getMarketingSiteOrigin } from '@/utils/hostOrigins';
import ArrangellyBrand from '@/components/brand/ArrangellyBrand';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useToast } from '@/components/ui/use-toast';
import PromoAmbient from '@/components/promo/PromoAmbient';
import {
  TEACHER_PROMO_NAV,
  STUDENT_PROMO_NAV,
  SUPERADMIN_PROMO_NAV,
  matchPromoNavPath,
  type PromoNavItem,
} from '@/components/promo/promoNav';

interface DashboardLayoutProps {
  children: React.ReactNode;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const role = (user?.role || '').toLowerCase();
  const isSuperadmin = role === 'superadmin';
  const isTeacher = role === 'teacher';
  const isStudent = role === 'student';
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [tenants, setTenants] = useState<TenantDto[]>([]);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(
    authService.getSelectedTenantId()
  );
  const [currentTenant, setCurrentTenant] = useState<TenantDto | null>(null);
  const navTrackRef = useRef<HTMLDivElement>(null);
  const navItemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [pillStyle, setPillStyle] = useState({ top: 0, height: 0 });

  const displayName =
    [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() ||
    user?.email?.split('@')[0] ||
    'User';

  const navItems: PromoNavItem[] = isSuperadmin
    ? SUPERADMIN_PROMO_NAV
    : isTeacher
      ? TEACHER_PROMO_NAV
      : isStudent
        ? STUDENT_PROMO_NAV
        : [];

  useEffect(() => {
    if (isSuperadmin) {
      void tenantService.getAllTenants().then(setTenants).catch(console.error);
    }
  }, [isSuperadmin]);

  useEffect(() => {
    if (selectedTenantId && isSuperadmin) {
      void tenantService
        .getTenant(selectedTenantId)
        .then(setCurrentTenant)
        .catch(console.error);
    }
  }, [selectedTenantId, isSuperadmin]);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  const updateNavPill = () => {
    const activeIndex = navItems.findIndex((item) =>
      matchPromoNavPath(location.pathname, item.path)
    );
    const el = navItemRefs.current[activeIndex >= 0 ? activeIndex : 0];
    const track = navTrackRef.current;
    if (!el || !track || activeIndex < 0) {
      setPillStyle({ top: 0, height: 0 });
      return;
    }
    setPillStyle({ top: el.offsetTop, height: el.offsetHeight });
  };

  useLayoutEffect(() => {
    updateNavPill();
    window.addEventListener('resize', updateNavPill);
    return () => window.removeEventListener('resize', updateNavPill);
  }, [location.pathname, navItems.length, mobileNavOpen]);

  const handleSwitchTenant = async (tenantId: string) => {
    try {
      await tenantService.switchTenant(tenantId);
      setSelectedTenantId(tenantId);
      toast({ title: 'Success', description: 'Tenant switched successfully' });
      setTimeout(() => window.location.reload(), 500);
    } catch (error: unknown) {
      const message =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      toast({
        title: 'Error',
        description: message || 'Failed to switch tenant',
        variant: 'destructive',
      });
    }
  };

  const sidebar = (
    <aside
      className={`promo-sidebar ${mobileNavOpen ? 'promo-sidebar--open' : ''}`}
    >
      <button
        type="button"
        className="promo-sidebar-close"
        onClick={() => setMobileNavOpen(false)}
        aria-label="Close menu"
      >
        <X size={22} strokeWidth={2} />
      </button>

      <button
        type="button"
        className="promo-side-logo"
        onClick={() => {
          window.location.href = getMarketingSiteOrigin();
        }}
        aria-label="Arrangelly home"
      >
        <ArrangellyBrand size="sm" layout="stacked" className="promo-side-brand" />
      </button>

      {isSuperadmin && (
        <div className="px-2 pb-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-between text-xs h-9 text-slate-700"
              >
                <span className="truncate">{currentTenant?.name || 'Select tenant'}</span>
                <Building2 className="w-3.5 h-3.5 shrink-0" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              <DropdownMenuLabel>Switch tenant</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {tenants.map((tenant) => (
                <DropdownMenuItem
                  key={tenant.id}
                  onClick={() => handleSwitchTenant(tenant.id)}
                >
                  {tenant.name}
                  {selectedTenantId === tenant.id && (
                    <span className="ml-auto text-violet-600">✓</span>
                  )}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      <div className="promo-nav-track" ref={navTrackRef}>
        <div
          className={`promo-nav-pill ${pillStyle.height > 0 ? 'on' : ''}`}
          style={{
            transform: `translateY(${pillStyle.top}px)`,
            height: pillStyle.height ? `${pillStyle.height}px` : 0,
          }}
        />
        {navItems.map((item, index) => {
          const hot = matchPromoNavPath(location.pathname, item.path);
          const Icon = item.icon;
          return (
            <button
              key={item.key}
              type="button"
              ref={(el) => {
                navItemRefs.current[index] = el;
              }}
              className={`promo-nav-item ${hot ? 'hot' : ''}`}
              onClick={() => navigate(item.path)}
            >
              <Icon strokeWidth={2} />
              {item.label}
            </button>
          );
        })}
      </div>

      <div className="promo-sidebar-foot">
        <p className="promo-sidebar-user">{displayName}</p>
        <button
          type="button"
          className="promo-logout"
          disabled={isLoggingOut}
          onClick={async () => {
            if (isLoggingOut) return;
            setIsLoggingOut(true);
            await logout();
          }}
        >
          {isLoggingOut ? 'Logging out…' : 'Logout'}
        </button>
      </div>
    </aside>
  );

  return (
    <div className="promo-app-root">
      <PromoAmbient />
      <div className="promo-stage">
        <div className="promo-shell">
          {!mobileNavOpen && (
            <div className="promo-mobile-bar">
              <button
                type="button"
                className="promo-menu-btn"
                onClick={() => setMobileNavOpen(true)}
                aria-label="Open menu"
              >
                <Menu size={20} />
              </button>
              <ArrangellyBrand size="sm" showSlogan={false} className="promo-mobile-brand" />
              <span className="w-8" aria-hidden />
            </div>
          )}
          <div className={`promo-app-grid ${mobileNavOpen ? 'promo-app-grid--nav-open' : ''}`}>
            {mobileNavOpen && (
              <button
                type="button"
                className="promo-nav-backdrop"
                onClick={() => setMobileNavOpen(false)}
                aria-label="Close menu"
              />
            )}
            {sidebar}
            <main className="promo-main">
              <div className="promo-main-inner">{children}</div>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardLayout;
